import * as XLSX from 'xlsx';

// Helper function to flatten nested JSON objects for Excel
const flattenObject = (obj: any, parentKey: string = '', res: { [key: string]: any } = {}) => {
  for (const key in obj) {
    const propName = parentKey ? `${parentKey}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      flattenObject(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
};

import { API_BASE } from './api';

export const exportUserData = async (authToken: string) => {
  try {
    const response = await fetch(`${API_BASE}/export-user-data`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }

    const data = await response.json();

    const workbook = XLSX.utils.book_new();

    // MoMs Sheet
    if (data.minutesOfMeetings && data.minutesOfMeetings.length > 0) {
      const momsSheet = XLSX.utils.json_to_sheet(data.minutesOfMeetings.map(flattenObject));
      XLSX.utils.book_append_sheet(workbook, momsSheet, 'Minutes of Meetings');
    }

    // Action Ledger Sheet
    if (data.actionLedger && data.actionLedger.length > 0) {
      const ledgerSheet = XLSX.utils.json_to_sheet(data.actionLedger);
      XLSX.utils.book_append_sheet(workbook, ledgerSheet, 'Action Ledger');
    }

    // Legacy Insights Sheet (preserved for backward compatibility)
    if (data.insights) {
      const insightsArray = Object.entries(data.insights).map(([key, value]) => ({
        insight: key,
        data: typeof value === 'object' ? JSON.stringify(value) : value,
      }));
      const insightsSheet = XLSX.utils.json_to_sheet(insightsArray);
      XLSX.utils.book_append_sheet(workbook, insightsSheet, 'Insights');
    }

    // Enhanced Analytics Sheets
    if (data.analytics) {
      // Analytics Overview Sheet
      if (data.analytics.overview) {
        const overviewData = [{
          'Total Sessions': data.analytics.overview.totalSessions || 0,
          'Sessions with MoM': data.analytics.overview.sessionsWithMoM || 0,
          'Total Chats': data.analytics.overview.totalChats || 0,
          'Total Action Items': data.analytics.overview.totalActionItems || 0,
          'Active Coaches': data.analytics.overview.activeCoaches || 0,
          'Last Session': data.analytics.overview.lastSessionAt || 'N/A',
          'Analysis Source': data.analytics.analysis_source || 'unknown',
          'Last Updated': data.analytics.last_updated || 'N/A'
        }];
        const overviewSheet = XLSX.utils.json_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Analytics Overview');
      }

      // Coaching Themes Sheet
      if (data.analytics.coaching_themes && data.analytics.coaching_themes.length > 0) {
        const themesData = data.analytics.coaching_themes.map((theme: string, index: number) => ({
          'Rank': index + 1,
          'Theme': theme,
          'Category': 'AI-Identified Theme'
        }));
        const themesSheet = XLSX.utils.json_to_sheet(themesData);
        XLSX.utils.book_append_sheet(workbook, themesSheet, 'Coaching Themes');
      }

      // Sentiment Trend Sheet
      if (data.analytics.sentiment_trend && data.analytics.sentiment_trend.length > 0) {
        const sentimentData = data.analytics.sentiment_trend.map((entry: any) => ({
          'Date': entry.date || 'N/A',
          'Sentiment Score': entry.score || 0,
          'Trend': entry.score > 0 ? 'Positive' : entry.score < 0 ? 'Negative' : 'Neutral'
        }));
        const sentimentSheet = XLSX.utils.json_to_sheet(sentimentData);
        XLSX.utils.book_append_sheet(workbook, sentimentSheet, 'Sentiment Trend');
      }

      // Coach Engagement Sheet
      if (data.analytics.coach_engagement_mix && Object.keys(data.analytics.coach_engagement_mix).length > 0) {
        const engagementData = Object.entries(data.analytics.coach_engagement_mix).map(([coach, sessions]: [string, unknown]) => ({
          'Coach': coach,
          'Sessions': sessions,
          'Percentage': data.analytics.overview.totalSessions > 0 
            ? `${(((sessions as number) / data.analytics.overview.totalSessions) * 100).toFixed(1)}%` 
            : '0%'
        }));
        const engagementSheet = XLSX.utils.json_to_sheet(engagementData);
        XLSX.utils.book_append_sheet(workbook, engagementSheet, 'Coach Engagement');
      }

      // Session Timeline Sheet
      if (data.analytics.session_timeline && data.analytics.session_timeline.length > 0) {
        const timelineData = data.analytics.session_timeline.map((session: any) => ({
          'Session ID': session.session_id || 'N/A',
          'Date': session.occurred_at ? new Date(session.occurred_at).toLocaleDateString() : 'N/A',
          'Title': session.title || 'Untitled Session',
          'Coach Type': session.coach_type || 'Unknown',
          'Has MoM': session.has_mom ? 'Yes' : 'No',
          'Key Takeaways Count': Array.isArray(session.key_takeaways) ? session.key_takeaways.length : 0,
          'Summary': session.summary || 'N/A'
        }));
        const timelineSheet = XLSX.utils.json_to_sheet(timelineData);
        XLSX.utils.book_append_sheet(workbook, timelineSheet, 'Session Timeline');
      }

      // Blindspot Alerts Sheet
      if (data.analytics.blindspot_alerts && data.analytics.blindspot_alerts.length > 0) {
        const blindspotsData = data.analytics.blindspot_alerts.map((alert: any, index: number) => ({
          'Alert #': index + 1,
          'Type': alert.type || 'General',
          'Alert': alert.alert || 'N/A'
        }));
        const blindspotsSheet = XLSX.utils.json_to_sheet(blindspotsData);
        XLSX.utils.book_append_sheet(workbook, blindspotsSheet, 'Blindspot Alerts');
      }
    }

    // Session Metrics Sheet
    if (data.sessionMetrics && data.sessionMetrics.length > 0) {
      const metricsData = data.sessionMetrics.map((session: any) => ({
        'Session ID': session.sessionId,
        'Coach Type': session.coachType || 'Unknown',
        'Message Count': session.messageCount || 0,
        'Has MoM': session.hasMom ? 'Yes' : 'No',
        'Created Date': session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'N/A',
        'Updated Date': session.updatedAt ? new Date(session.updatedAt).toLocaleDateString() : 'N/A'
      }));
      const metricsSheet = XLSX.utils.json_to_sheet(metricsData);
      XLSX.utils.book_append_sheet(workbook, metricsSheet, 'Session Metrics');
    }

    XLSX.writeFile(workbook, 'HiPo_AI_Coach_Analytics_Export.xlsx');

  } catch (error) {
    console.error('Error exporting user data:', error);
    // You might want to show a toast notification for the error here
  }
};
